<template>
    <div class="location-finder row" @keypress.enter.prevent>
        <div class="col-md-12" v-show="search">
            <div class="form-group">
                <label>{{ 'core.address' | trans }}</label>
                <input ref="locationInput" class="form-control location-input" type="text" :placeholder="'frontend.location_finder.enter_location' | trans">
            </div>
        </div>
        <div class="col-md-12" v-show="!search" v-if="!disableInput">
            <slot>
                <div class="form-group">
                    <label>{{ 'core.address' | trans }} 1</label>
                    <input v-model="location.address" @change="$emit('input', location)" class="form-control">
                </div>
                <div class="form-group">
                    <label>{{ 'core.address' | trans }} 2</label>
                    <input v-model="location.address2" @change="$emit('input', location)" class="form-control">
                </div>
                <div class="form-group">
                    <label>{{ 'core.city' | trans }}</label>
                    <input v-model="location.city" @change="$emit('input', location)" class="form-control">
                </div>
                <div class="form-row clearfix">
                    <div class="col-xs-6">
                        <label>{{ 'core.region' | trans }}</label>
                        <input v-model="location.region" @change="$emit('input', location)" class="form-control">
                    </div>
                    <div class="col-xs-6">
                        <label>{{ 'core.zip_code' | trans }}</label>
                        <input v-model="location.postcode" @change="$emit('input', location)" class="form-control">
                    </div>
                </div>
                <div class="form-group">
                    <label>{{ 'core.country' | trans }}</label>
                    <select v-model="location.country" class="form-control">
                        <option v-for="(country, code) in countries" :value="code">
                            {{ country }}
                        </option>
                    </select>
                </div>
            </slot>
        </div>
        <div class="col-md-12" :class="{'col-md-12': disableInput}" v-show="!search">
            <a href @click.prevent="search = true">
                {{ 'frontend.location_finder.reset_location' | trans }}
            </a>
            <div ref="map" class="location-map"></div>
        </div>

    </div>
</template>

<style>
.location-finder {
    margin-bottom: 15px;
}

.location-map {
    min-height: 180px;
}

.form-row {
    margin-left: -15px;
    margin-right: -15px;
    margin-bottom: 15px;
}
</style>

<script>
/* global google */
// reference: https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete
import countries from './countries.js';
const ZOOM = 16;
export default {
    props: {
        value: {
            type: Object,
            default () {
                return {};
            },
        },
        disableInput: {
            default: false,
            type: Boolean
        },
    },
    methods: {
        init() {
            if (!this.$refs.map) {
                //                    sometimes ref is undefined ?
                return;
            }
            var map = new google.maps.Map(this.$refs.map, {
                center: {
                    lat: -33.8688,
                    lng: 151.2195
                },
                zoom: ZOOM
            });
            this.map = map;

            let options = {
                types: ['address'],  // https://developers.google.com/places/supported_types#table3
                // (cities) (regions) geocode  address establishment
                componentRestrictions: {country: ['rs','hr','me']}
            };

            var autocomplete = new google.maps.places.Autocomplete(this.$refs.locationInput, options);
            autocomplete.bindTo('bounds', map);
            this.marker = new google.maps.Marker({
                map: map,
                draggable: true,
                anchorPoint: new google.maps.Point(0, -29)
            });
            this.marker.addListener('mouseup', () => {
                const pos = this.marker.getPosition();
                this.map.setCenter(pos);
                this.location.longitude = pos.lng();
                this.location.latitude = pos.lat();
                this.$emit('input', this.location);
            });
            // TODO: Validate required parameters e.g. lng, lat
            this.setExternalValue();
            this.listenerHandle = autocomplete.addListener('place_changed', function() {
                this.hideMarker();
                var place = autocomplete.getPlace();
                if (!place.geometry) {
                    // User entered the name of a Place that was not suggested and
                    // pressed the Enter key, or the Place Details request failed.
                    window.alert('No details available for input: \'' + place.name + '\'');
                    return;
                }
                this.location.address = [
                    (this.getType(place, 'route') || this.getType(place, 'neighborhood') || {}).long_name,
                    (this.getType(place, 'street_number') || {}).long_name
                ].filter(x => x).join(', ');
                // loc.address2
                this.location.city = (this.getType(place, 'locality') || this.getType(place, 'administrative_area_level_1', {})).long_name;
                this.location.country = this.getType(place, 'country', {}).short_name;
                this.location.region = (this.getType(place, 'administrative_area_level_1') || this.getType(place, 'administrative_area_level_2', {})).short_name;
                this.location.postcode = this.getType(place, 'postal_code', {}).short_name;
                this.location.longitude = place.geometry.location.lng();
                this.location.latitude = place.geometry.location.lat();
                this.search = false;
                this.$emit('input', this.location);
                google.maps.event.addListenerOnce(map, 'idle', () => {
                    if (place.geometry.viewport) {
                        map.fitBounds(place.geometry.viewport);
                    } else {
                        map.setCenter(place.geometry.location);
                    }
                    // debugger;
                    this.setMarker(place.geometry.location);
                    map.setZoom(ZOOM);
                    google.maps.event.trigger(map, 'resize');
                });
            }.bind(this));
        },
        getType(place, findType, defaultVal) {
            return place.address_components.find(component => {
                return component.types.find(type => type == findType);
            }) || defaultVal;
        },
        libraryLoaded() {
            return typeof google !== 'undefined' && google.maps && google.maps.Map;
        },
        setMarker(position) {
            this.marker.setPosition(position);
            this.marker.setVisible(true);
        },
        hideMarker() {
            this.marker.setVisible(false);
        },
        setExternalValue() {
            if (this.libraryLoaded() && this.value && this.value.longitude && this.value.latitude) {
                this.location = this.value;
                this.map.setCenter({
                    lat: this.location.latitude,
                    lng: this.location.longitude
                });
                this.map.setZoom(ZOOM);
                this.setMarker({
                    lat: this.location.latitude,
                    lng: this.location.longitude
                });
                this.search = false;
                setTimeout(() => {
                    google.maps.event.trigger(this.map, 'resize');
                }, 0);
            } else {
                this.location = {};
                this.$refs.locationInput.value = '';
                this.search = true;
            }
        }
    },
    data() {
        return {
            search: true,
            countries: countries,
            listenerHandle: null,
            dropdownClickHandle: null,
            map: null,
            marker: null,
            location: {
                address: '',
                address2: '',
                city: '',
                region: '',
                postcode: '',
                country: '',
                longitude: null,
                latitude: null
            },
        };
    },
    watch: {
        value() {
            this.setExternalValue();
        }
    },
    mounted() {
        if (this.libraryLoaded()) {
            this.init();
            return;
        }
        this.dropdownClickHandle = (e) => {
            let container = document.getElementsByClassName('pac-container')[0];
            if (e.target == container || isDescendant(container, e.target)) {
                this.$emit('place-click', e);
            }
        };
        document.addEventListener('mousedown', this.dropdownClickHandle, true);

        function isDescendant(parent, child) {
            var node = child.parentNode;
            while (node != null) {
                if (node == parent) {
                    return true;
                }
                node = node.parentNode;
            }
            return false;
        }
        if (!window.initMap) {
            window.initMap = function() {
                window.initMap.callbacks.forEach(cb => {
                    cb();
                });
                window.initMap.callbacks = [];
            };
            window.initMap.callbacks = [];
            let sc = document.createElement('script');
            sc.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyB3_5OobxRvZDgi6JRkUBdcwgSNBtD3pmA&libraries=places&callback=initMap';
            document.getElementsByTagName('head')[0].appendChild(sc);
        }
        window.initMap.callbacks.push(this.init);
    },
    destroyed() {
        if (this.listenerHandle && this.libraryLoaded()) {
            google.maps.event.removeListener(this.listenerHandle);
        }
        if (this.dropdownClickHandle) {
            document.removeEventListener('mousedown', this.dropdownClickHandle);
        }
    }
};
</script>


// WEBPACK FOOTER //
// resources/js/ui/LocationFinder.vue